<template>
	<div ref="scroll" class="horizontal-scroll" :scroll-index="index">
        <div ref="scrollContainer" class="horizontal-scroll__target-wrapper horizontal-scroll__target">
            <slot name="scrolled-content"></slot>
        </div>
        <div v-if="isOverflow" class="horizontal-scroll__progress-wrapper" :class="`horizontal-scroll__line-${lineColor}`">
            <div ref="progress" class="horizontal-scroll__progress" :class="`horizontal-scroll__progress-${sliderColor}`"></div>
        </div>
    </div>
</template>

<script>
    import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';
    import fastdom from 'fastdom';
    import { useResizeObserver, useScroll } from '@vueuse/core';

    export default {
		name: 'horizontalScroll',
		props: {
            content: {
                type: String,
                default: '',
			},
            index: {
                type: String,
                default: '',
                required: true,
            },
            lineColor: {
                type: String,
                default: '',
                required: true,
            },
            sliderColor: {
                type: String,
                default: '',
                required: true,
            },
            noSlider: {
                type: Boolean,
                default: false,
            },
		},
        data() {
            return {
                scrollBarsIndexesError: false,
            }
        },
        setup(props) {
            const scroll = ref(null);
            const progress = ref(null);
            const scrollChildren = shallowRef([]);
            const scrollContainer = ref(null);
            const scrollContainerWidth = ref(0);
            const scrollOffsetWidth = ref(0);
            const noSlider = props.noSlider;

            const getChildren = () => {
                if (!scrollContainer.value?.children) return;
                scrollChildren.value = Array.from(scrollContainer.value.children);
            };

            useResizeObserver(scroll, ([entry]) => {
                if (entry.borderBoxSize?.[0]) { // supported ios >=15.4
                    const { inlineSize } = entry.borderBoxSize[0];
                    scrollOffsetWidth.value = inlineSize;
                } else {
                    fastdom.measure(() => {
                        const { width } = entry.target.getBoundingClientRect();
                        scrollOffsetWidth.value = width;
                    });
                }
            }, { box: 'border-box' });

            useResizeObserver(scrollContainer, () => {
                scrollContainerWidth.value = scrollContainer.value?.scrollWidth ?? 0;
            });

            const isOverflow = computed(() => {
                return !noSlider && (scrollContainerWidth.value > scrollOffsetWidth.value);
            });

            const observer = new MutationObserver(() => {
                getChildren();
            });

            const toggleScrolledContentMargin = () => {
                scrollChildren.value.forEach((el) => {
                    el.classList.toggle('horizontal-scroll__margin', isOverflow.value);
                });
            };

            watch([scrollChildren, isOverflow], () => {
                toggleScrolledContentMargin();
            });

            onMounted(() => {
                getChildren();
                observer.observe(scroll.value, { childList: true, subtree: true });
            });

            onUnmounted(() => {
                observer.disconnect();
            });

            const { x: xScroll } = useScroll(scrollContainer);

            watch(xScroll, (val) => {
                if (noSlider) return;
                const percent = val / (scrollContainerWidth.value - scrollOffsetWidth.value) * 67;
                progress.value.style.left = percent + '%';
            });

            return {
                isOverflow,
                scroll,
                progress,
                xScroll,
                scrollOffsetWidth,
                scrollContainerWidth,
                scrollContainer,
            };
        },
	}
</script>

<style lang="scss">
    @import "/local/templates/redesign/src/assets/scss/base/_variables.scss";
    @import "/local/templates/redesign/src/assets/scss/base/_mixins.scss";
    @import "/local/templates/redesign/src/assets/scss/base/_functions.scss";

    $b: '.horizontal-scroll';

    #{$b} {
        position: relative;

        &__target {

            &::-webkit-scrollbar {
                display: none;
            }

            &-wrapper {

                @include mobile {
                    overflow-x: scroll;
                    overflow-y: hidden;
                }
            }
        }

        &__margin {
            @include fluid('margin-bottom', 0, 16px);
        }

        &__line {

            &-black {
                background-color: $grey-1514;
            }

            &-gray-fourth {
                background-color: $grey-109;
            }
        }

        &__progress {
            position: absolute;
            background-color: $white-101;
            @include fluid('width', 0, 40px);
            @include fluid('height', 0, 8px);
            @include fluid('border-radius', 0, 4px);

            &-wrapper {
                position: absolute;
                @include fluid('left', 0, 100px);
                @include fluid('width', 0, 120px);
                @include fluid('height', 0, 8px);
                @include fluid('border-radius', 0, 4px);

                // customize scrollbar position in separate style list if needed
                @include fluid('bottom', 0);
                //

                &._error {
                    background-color: red !important;
                }
            }

            &-white {
                background-color: $white-101;
            }

            &-black {
                background-color: $grey-1514;
            }

            &-gray-french {
                background-color: $grey-166;
            }
        }
    }
</style>
